// src/pages/admin/InvenireAdminDashboard.jsx
// Super Admin Dashboard - standardized design with dynamic client selector

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { trackPageView } from "../../utils/analytics";

const API_BASE = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE || "https://api.invenire.app";

// Invenire brand color
const INVENIRE_PRIMARY = "#a855f7";

// Card accent colors
const COLORS = {
  purple: "#a855f7",
  blue: "#3b82f6",
  green: "#22c55e",
  red: "#f87171",      // Soft red (available for future use)
  cyan: "#06b6d4",
  orange: "#f59e0b",
  pink: "#ec4899"
};

export default function InvenireAdminDashboard() {
  const navigate = useNavigate();
  
  const [clients, setClients] = useState([]);
  const [selectedClientKey, setSelectedClientKey] = useState("");
  const [recentJobs, setRecentJobs] = useState([]);
  const [stats, setStats] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(true);

  // Set document title
  useEffect(() => {
    document.title = 'Invenire - Admin Dashboard';
  }, []);

  // Track page view on mount
  useEffect(() => {
    trackPageView('/admin', 'Invenire Admin Dashboard');
  }, []);

  // Load clients and stats
  useEffect(() => {
    async function loadData() {
      try {
        // Load clients
        const clientsResponse = await fetch(`${API_BASE}/tenants`);
        if (clientsResponse.ok) {
          const data = await clientsResponse.json();
          const list = Array.isArray(data) ? data : (data.tenants || []);
          setClients(list);
          
          // Auto-select first client
          if (list.length > 0) {
            setSelectedClientKey(list[0].key);
          }
        }
        
        // Load recent jobs (across all tenants)
        const jobsResponse = await fetch(`${API_BASE}/scoring/summary`);
        if (jobsResponse.ok) {
          const data = await jobsResponse.json();
          setRecentJobs((data.jobs || []).slice(0, 5));
          setStats(data.summary || null);
        }
      } catch (err) {
        console.error("Failed to load data:", err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const selectedClient = clients.find(c => c.key === selectedClientKey);

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.logoContainer}>
          <img 
            src="/invenire-logo-transparent.png" 
            alt="Invenire" 
            style={styles.logoImage}
          />
          <span style={styles.subtitle}>Admin Dashboard</span>
        </div>
        <button onClick={() => navigate("/")} style={styles.backButton}>
          Back to Site
        </button>
      </div>
      
      {/* Main Content */}
      <div style={styles.content}>
        <h2 style={styles.welcomeTitle}>Welcome to Invenire Admin</h2>
        <p style={styles.welcomeText}>
          Manage all clients, configure interview experiences, and monitor the entire platform.
        </p>
        
        {/* Quick Actions */}
        <div style={styles.cardsGrid}>
          {/* All Clients */}
          <div 
            style={{ ...styles.card, borderColor: COLORS.purple }}
            onClick={() => navigate("/admin/clients")}
          >
            <span style={{ ...styles.cardLabel, color: COLORS.purple }}>PLATFORM</span>
            <h3 style={styles.cardTitle}>All Clients</h3>
            <p style={styles.cardText}>
              View and manage all client configurations, branding, and interview settings.
            </p>
            <div style={{ ...styles.cardButton, backgroundColor: COLORS.purple, color: "#fff" }}>
              Manage Clients
            </div>
          </div>
          
          {/* Job Loader */}
          <div 
            style={{ ...styles.card, borderColor: COLORS.green }}
            onClick={() => navigate("/admin/jobs")}
          >
            <span style={{ ...styles.cardLabel, color: COLORS.green }}>CONFIGURATION</span>
            <h3 style={styles.cardTitle}>Job Loader</h3>
            <p style={styles.cardText}>
              Import job postings from any URL. AI extracts titles, requirements, and responsibilities.
            </p>
            <div style={{ ...styles.cardButton, backgroundColor: COLORS.green, color: "#11181C" }}>
              Load Jobs
            </div>
          </div>
          
          {/* Candidate Upload */}
          <div 
            style={{ ...styles.card, borderColor: COLORS.blue }}
            onClick={() => navigate("/admin/candidates")}
          >
            <span style={{ ...styles.cardLabel, color: COLORS.blue }}>INTERVIEWS</span>
            <h3 style={styles.cardTitle}>Candidate Upload</h3>
            <p style={styles.cardText}>
              Upload candidates for any client. Select a client, choose a job, and generate invitations.
            </p>
            <div style={{ ...styles.cardButton, backgroundColor: COLORS.blue, color: "#fff" }}>
              Upload Candidates
            </div>
          </div>
          
          {/* Reports & Analytics */}
          <div 
            style={{ ...styles.card, borderColor: COLORS.pink }}
            onClick={() => navigate("/admin/reports")}
          >
            <span style={{ ...styles.cardLabel, color: COLORS.pink }}>INSIGHTS</span>
            <h3 style={styles.cardTitle}>Reports & Analytics</h3>
            <p style={styles.cardText}>
              View candidate scores, interview analytics, and AI-powered insights across all clients.
            </p>
            <div style={{ ...styles.cardButton, backgroundColor: COLORS.pink, color: "#fff" }}>
              View Reports
            </div>
          </div>
          
          {/* User Management */}
          <div 
            style={{ ...styles.card, borderColor: COLORS.orange }}
            onClick={() => navigate("/admin/users")}
          >
            <span style={{ ...styles.cardLabel, color: COLORS.orange }}>ACCESS CONTROL</span>
            <h3 style={styles.cardTitle}>User Management</h3>
            <p style={styles.cardText}>
              Manage platform users, approve access requests, and configure permissions.
            </p>
            <div style={{ ...styles.cardButton, backgroundColor: COLORS.orange, color: "#11181C" }}>
              Manage Users
            </div>
          </div>
          
          {/* Paused Interviews */}
          <div 
            style={{ ...styles.card, borderColor: COLORS.cyan }}
            onClick={() => navigate("/admin/paused")}
          >
            <span style={{ ...styles.cardLabel, color: COLORS.cyan }}>RECOVERY</span>
            <h3 style={styles.cardTitle}>Paused Interviews</h3>
            <p style={styles.cardText}>
              Manage interviews that were interrupted. Send reconnect links to candidates.
            </p>
            <div style={{ ...styles.cardButton, backgroundColor: COLORS.cyan, color: "#11181C" }}>
              View Paused
            </div>
          </div>
        </div>
        
        {/* Quick Access: Job Reports */}
        {recentJobs.length > 0 && (
          <div style={styles.quickAccessSection}>
            <h3 style={styles.quickAccessTitle}>Quick Access: Job Reports</h3>
            <div style={styles.quickAccessButtons}>
              {recentJobs.map(job => (
                <button
                  key={job.id}
                  onClick={() => navigate(`/admin/jobs/${job.id}/report`)}
                  style={styles.quickAccessButton}
                >
                  📊 {job.title?.slice(0, 25) || `Job ${job.id}`}
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* Client Admin Portals */}
        <div style={styles.clientSection}>
          <h3 style={styles.quickAccessTitle}>Client Admin Portals</h3>
          <p style={styles.clientSectionText}>
            Access client-specific admin dashboards with branded experiences.
          </p>
          
          <div style={styles.clientSelector}>
            <select
              value={selectedClientKey}
              onChange={(e) => setSelectedClientKey(e.target.value)}
              style={styles.clientSelect}
            >
              {clients.length === 0 ? (
                <option value="">No clients available</option>
              ) : (
                clients.map(client => (
                  <option key={client.key} value={client.key}>
                    {client.name || client.key}
                  </option>
                ))
              )}
            </select>
            
            <button
              onClick={() => {
                if (selectedClientKey) {
                  navigate(`/client/${selectedClientKey}`);
                }
              }}
              disabled={!selectedClientKey}
              style={{
                ...styles.clientButton,
                backgroundColor: selectedClientKey ? INVENIRE_PRIMARY : "#6b7280",
                cursor: selectedClientKey ? "pointer" : "not-allowed"
              }}
            >
              Open {selectedClient?.name || "Client"} Admin →
            </button>
          </div>
        </div>
        
        {/* Platform Stats */}
        {stats && (
          <div style={styles.statsSection}>
            <h3 style={styles.statsTitle}>Platform Overview</h3>
            <div style={styles.statsGrid}>
              <div style={styles.statCard}>
                <div style={styles.statNumber}>{clients.length}</div>
                <div style={styles.statLabel}>Total Clients</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statNumber}>{stats.total_jobs || 0}</div>
                <div style={styles.statLabel}>Total Jobs</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statNumber}>{stats.total_interviews || 0}</div>
                <div style={styles.statLabel}>Total Interviews</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statNumber}>{stats.total_analyzed || 0}</div>
                <div style={styles.statLabel}>Analyzed</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statNumber}>
                  {stats.average_score ? stats.average_score.toFixed(1) : "—"}
                </div>
                <div style={styles.statLabel}>Avg Score</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// STYLES
// =============================================================================

const styles = {
  container: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #11181C 0%, #1a2632 50%, #0a0f14 100%)",
    color: "#fff",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px 40px",
    borderBottom: "1px solid rgba(255,255,255,0.1)"
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    margin: 0,
    color: INVENIRE_PRIMARY
  },
  subtitle: {
    fontSize: 14,
    color: "#9ca3af"
  },
  logoContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 4
  },
  logoImage: {
    height: 64,
    width: "auto",
    objectFit: "contain"
  },
  backButton: {
    padding: "8px 16px",
    background: "rgba(255,255,255,0.1)",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    fontSize: 14,
    cursor: "pointer"
  },
  content: {
    maxWidth: 1000,
    margin: "0 auto",
    padding: "40px 20px"
  },
  welcomeTitle: {
    fontSize: 28,
    marginBottom: 10
  },
  welcomeText: {
    color: "#9ca3af",
    marginBottom: 40,
    fontSize: 16
  },
  cardsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: 24,
    marginBottom: 40
  },
  card: {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 12,
    padding: 24,
    transition: "transform 0.2s, border-color 0.2s",
    cursor: "pointer"
  },
  cardLabel: {
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: 1
  },
  cardTitle: {
    fontSize: 20,
    margin: "12px 0 8px",
    color: "#fff"
  },
  cardText: {
    color: "#9ca3af",
    fontSize: 14,
    marginBottom: 20,
    lineHeight: 1.5
  },
  cardButton: {
    display: "inline-block",
    padding: "10px 20px",
    borderRadius: 6,
    fontWeight: 600,
    fontSize: 14
  },
  quickAccessSection: {
    marginBottom: 40
  },
  quickAccessTitle: {
    fontSize: 14,
    color: "#9ca3af",
    textTransform: "uppercase",
    letterSpacing: 1,
    marginBottom: 16
  },
  quickAccessButtons: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap"
  },
  quickAccessButton: {
    padding: "8px 16px",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 6,
    color: "#fff",
    fontSize: 14,
    cursor: "pointer"
  },
  clientSection: {
    marginBottom: 40,
    background: "rgba(255,255,255,0.02)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 12,
    padding: 24
  },
  clientSectionText: {
    color: "#6b7280",
    fontSize: 14,
    marginBottom: 16
  },
  clientSelector: {
    display: "flex",
    gap: 12,
    alignItems: "center",
    flexWrap: "wrap"
  },
  clientSelect: {
    padding: "12px 16px",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 6,
    color: "#fff",
    fontSize: 14,
    minWidth: 200
  },
  clientButton: {
    padding: "12px 20px",
    border: "none",
    borderRadius: 6,
    color: "#fff",
    fontSize: 14,
    fontWeight: 600
  },
  statsSection: {
    marginTop: 40
  },
  statsTitle: {
    fontSize: 14,
    color: "#9ca3af",
    textTransform: "uppercase",
    letterSpacing: 1,
    marginBottom: 20
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: 16
  },
  statCard: {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 8,
    padding: 20,
    textAlign: "center"
  },
  statNumber: {
    fontSize: 32,
    fontWeight: 700,
    color: "#fff"
  },
  statLabel: {
    fontSize: 12,
    color: "#6b7280",
    marginTop: 4
  }
};
